import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BasicInfoStep, AccrualSetupStep, SummaryStep } from "./steps";
import { CreateTimeoffRuleRequestType, UpdateTimeOffRuleRequest } from "../../types/request/timeoff";
import { RootState, useAppDispatch } from "../../redux/store";
import { getAllTimeoffCategories, getAssignedTimeoffCategories } from "../../redux/features/common";
import { TimeOffAccrualPeriodEnum, TimeoffPolicyTypeEnum } from "../../enums";
import { createTimeoffPolicy, updateTimeoffPolicy } from "../../redux/features/timeoff";
import { TimeoffPolicyType } from "../../types";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { ChevronLeft } from "lucide-react";

const CreateTimeoffRule = () => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const dispatch = useAppDispatch();
    const { isPolicyEditMode, selectedPolicyToEdit } = useSelector((state: RootState) => state.timeoffCategory);
    const { isUpdatingTimeoff } = useSelector((state: RootState) => state.timeoff);

    useEffect(() => {
        dispatch(getAllTimeoffCategories());
    }, [dispatch]);

    const policyTypes: TimeoffPolicyType[] = [
        {
            code: "flexible",
            name: "Flexible",
        },
        {
            code: "accrues_time",
            name: "Accrues Time",
        },
    ];

    const initformData: CreateTimeoffRuleRequestType = isPolicyEditMode
        ? {
              policy_code: selectedPolicyToEdit?.us_user_time_off_policy_code,
              policy_name: selectedPolicyToEdit?.us_user_time_off_policy_name,
              category_code: selectedPolicyToEdit?.us_user_time_off_category_code,
              policy_type: selectedPolicyToEdit?.policy_type,
              accrual_rate: Number(selectedPolicyToEdit?.accrual_rate ?? 0),
              accrual_period: selectedPolicyToEdit?.accrual_period as TimeOffAccrualPeriodEnum,
              accrual_frequency_json: selectedPolicyToEdit?.accrual_frequency_json
                  ? JSON.parse(selectedPolicyToEdit.accrual_frequency_json)
                  : {},
              has_waiting_period: selectedPolicyToEdit?.has_waiting_period === "true",
              waiting_period_json: selectedPolicyToEdit?.waiting_period_json
                  ? JSON.parse(selectedPolicyToEdit.waiting_period_json)
                  : undefined,
              cap_enabled: selectedPolicyToEdit?.cap_enabled === "true",
              cap_hours_limit: Number(selectedPolicyToEdit?.cap_hours_limit ?? 0),
              carryover_enabled: selectedPolicyToEdit?.carryover_enabled === "true",
              carryover_json: selectedPolicyToEdit?.carryover_json
                  ? JSON.parse(selectedPolicyToEdit.carryover_json)
                  : undefined,
              fine_tuning_json: selectedPolicyToEdit?.fine_tuning_json
                  ? JSON.parse(selectedPolicyToEdit.fine_tuning_json)
                  : undefined,
              milestones_json: selectedPolicyToEdit?.milestones_json
                  ? JSON.parse(selectedPolicyToEdit.milestones_json)
                  : undefined,
          }
        : {
              cap_enabled: false,
              policy_code: "",
              accrual_period: TimeOffAccrualPeriodEnum.WEEKLY,
              policy_type: TimeoffPolicyTypeEnum.flexible,
              accrual_rate: 0,
              has_waiting_period: false,
              cap_hours_limit: 0,
              carryover_enabled: false,
              accrual_frequency_json: {},
          };

    // Form Data State
    const [formData, setFormData] = useState<CreateTimeoffRuleRequestType>(initformData);

    // Determine Steps Dynamically Based on Policy Type
    const steps =
        formData.policy_type === TimeoffPolicyTypeEnum.accrues_time
            ? ["Basic Info", "Accrual Setup", "Summary"]
            : ["Basic Info", "Summary"];

    const handleNext = () => {

        if (activeStep === 0) {
            if (!formData.policy_type || !formData.policy_code || !formData.category_code || !formData.policy_name) {
                toast.dismiss();
                toast.error("Please fill in all the required fields.", {
                    position: "top-center",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                return;
            }
        }

        if (activeStep === steps.length - 1) {
            onSubmit();
        } else {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

    const onSubmit = async () => {
        // alert(`Submitting Policy: ${JSON.stringify(formData, null, 2)}`);
        let response;

        if (isPolicyEditMode) {
            const request: UpdateTimeOffRuleRequest = {
                time_off_policy_id: Number(selectedPolicyToEdit?.us_user_time_off_policy_id),
                ...formData,
            };
            response = await dispatch(updateTimeoffPolicy(request)).unwrap();
        } else {
            response = await dispatch(createTimeoffPolicy(formData)).unwrap();
        }

        if (response.status) {
            toast.dismiss();
            toast.success("Policy created successfully.");
        } else {
            toast.dismiss();
            toast.error(response.message);
        }
        navigate("/time-off-policies");
    };

    const renderStepContent = (step: number) => {
        if (step === 0)
            return <BasicInfoStep formData={formData} setFormData={setFormData} policyTypes={policyTypes} />;
        if (formData.policy_type === TimeoffPolicyTypeEnum.accrues_time) {
            if (step === 1) return <AccrualSetupStep formData={formData} setFormData={setFormData} />;
            if (step === 2) return <SummaryStep formData={formData} />;
        } else {
            if (step === 1) return <SummaryStep formData={formData} />;
        }
        return null;
    };
    return (
        <div className="w-full h-full p-6 bg-gray-100 flex flex-col overflow-y-auto">
            {/* Header */}
            <div className="flex items-center mb-4">
                <button
                    onClick={() => window.history.back()}
                    className="text-[#3788d8] hover:text-[#2a6ebb] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2a6ebb]"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="text-xl font-bold text-[#3788d8] ml-4">
                    {isPolicyEditMode ? "Update Policy" : "Add Policy"}
                </h3>
            </div>

            {/* Title */}
            {/* <h1 className="text-3xl font-bold text-blue-600 mt-4">New Policy</h1> */}

            {/* Stepper */}
            <div className="flex items-center justify-evenly bg-white p-4 mt-4 rounded">
                {steps.map((label, index) => (
                    <div key={label} className="flex items-center">
                        {/* Step Circle */}
                        <div
                            className={`relative flex items-center justify-center w-10 h-10 rounded-full text-white font-bold transition-all
                        ${activeStep === index ? "bg-blue-500 scale-110 shadow-lg" : "bg-gray-300"}
                        ${activeStep > index ? "bg-green-500" : ""}`}
                            onClick={() => setActiveStep(index)}
                        >
                            {index + 1}
                        </div>

                        {/* Step Label */}
                        <div
                            className={`ml-2 text-sm font-medium transition-all
                        ${activeStep === index ? "text-blue-500 font-bold" : "text-gray-600"}
                        ${activeStep > index ? "text-green-500" : ""}`}
                            onClick={() => setActiveStep(index)}
                        >
                            {label}
                        </div>

                        {/* Step Connector */}
                        {index < steps.length - 1 && (
                            <div
                                className={`flex-1 h-1 mx-2 transition-all 
                            ${activeStep > index ? "bg-green-500" : "bg-gray-300"}`}
                            ></div>
                        )}
                    </div>
                ))}
            </div>

            {/* Step Content */}
            <div className="mt-6">{renderStepContent(activeStep)}</div>

            {/* Navigation Buttons */}
            <div className="flex justify-between bg-white p-4 shadow mt-6">
                {!isUpdatingTimeoff && (
                    <button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        className="bg-gray-300 px-4 py-2 rounded disabled:opacity-50"
                    >
                        Back
                    </button>
                )}
                <button
                    onClick={activeStep === steps.length - 1 ? onSubmit : handleNext}
                    className={`px-4 py-2 rounded flex items-center justify-center gap-2
            ${activeStep === steps.length - 1 ? "bg-green-600 text-white" : "bg-blue-500 text-white"}
            ${isUpdatingTimeoff ? "opacity-60 cursor-not-allowed" : ""}
        `}
                    disabled={isUpdatingTimeoff}
                >
                    {isUpdatingTimeoff && (
                        <svg
                            className="animate-spin h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            ></path>
                        </svg>
                    )}
                    {activeStep === steps.length - 1 ? (isUpdatingTimeoff ? "Updating..." : "Submit") : "Next"}
                </button>
            </div>
        </div>
    );
};

export default CreateTimeoffRule;
